import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { sessionManager } from '../../config/sessionManager';
import { PermissionLevel } from '../../config/permissions';
import ShareButton from '../../components/ShareButton';

// Unified responsive game header (mobile + web) mirroring SeasonHeader pattern
const GameHeader: React.FC = () => {
  const { gameId } = useParams();
  const location = useLocation();
  const basePath = `/games/${gameId}`;
  const seasonName = sessionManager.getSeasonName();
  const gameName = sessionManager.getGameName() || 'Game';
  const shortGameName = gameName.length > 9 ? `${gameName.slice(0, 9)}...` : gameName;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  // Mobile tab paging (4 + 4)
  const [tabPage, setTabPage] = useState<0 | 1>(0);

  // Check permissions for game menu items
  const canAccessStats = sessionManager.hasPermission('games.gameMenu.stats', PermissionLevel.VIEW);
  const canAccessDetails = sessionManager.hasPermission('games.gameMenu.details', PermissionLevel.VIEW);
  const canAccessKPIs = sessionManager.hasPermission('games.gameMenu.kpis', PermissionLevel.VIEW);

  const allTabs = [
    {
      to: `${basePath}/stats`,
      label: 'Stats',
      active: (p: string) => /\/stats$/.test(p),
      canAccess: canAccessStats,
    },
    {
      to: `${basePath}/roles`,
      label: 'Roles',
      active: (p: string) => /\/roles$/.test(p),
      canAccess: true, // Roles is always visible (not in permissions)
    },
    {
      to: `${basePath}/teams`,
      label: 'Teams',
      active: (p: string) => /\/teams$/.test(p),
      canAccess: true, // Everyone can access Teams
    },
    {
      to: `${basePath}/players`,
      label: 'Players',
      active: (p: string) => /\/players$/.test(p),
      canAccess: true, // Everyone can access Players
    },
    {
      to: `${basePath}/details`,
      label: 'Details',
      active: (p: string) => /\/details$/.test(p),
      canAccess: canAccessDetails,
    },
    {
      to: `${basePath}/roster`,
      label: 'Roster',
      active: (p: string) => /\/roster$/.test(p),
      canAccess: true, // Everyone can access Roster
    },
    {
      to: `${basePath}/kpis`,
      label: 'KPIs',
      active: (p: string) => /\/kpis$/.test(p),
      canAccess: canAccessKPIs,
    },
    {
      to: `${basePath}/periods`,
      label: 'Periods',
      active: (p: string) => /\/periods$/.test(p),
      canAccess: true, // Everyone can access Periods
    },
  ];

  // Filter tabs based on permissions
  const tabs = useMemo(
    () => allTabs.filter((tab) => tab.canAccess),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canAccessStats, canAccessDetails, canAccessKPIs],
  );

  const primaryTabLabels = ['Stats', 'Roles', 'Teams', 'Players'] as const;
  const secondaryTabLabels = ['Details', 'Roster', 'KPIs', 'Periods'] as const;

  // Filter visible tabs for mobile paging
  const primaryTabs = useMemo(
    () => tabs.filter((t) => primaryTabLabels.includes(t.label as any)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs],
  );
  const secondaryTabs = useMemo(
    () => tabs.filter((t) => secondaryTabLabels.includes(t.label as any)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs],
  );

  // Ensure the current page shows the active tab when route changes (mobile)
  useEffect(() => {
    const activeLabel = tabs.find((t) => t.active(location.pathname))?.label;
    if (!activeLabel) return;
    if (primaryTabLabels.includes(activeLabel as any)) setTabPage(0);
    else if (secondaryTabLabels.includes(activeLabel as any)) setTabPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tabs]);

  const recalc = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 5);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };
  useEffect(() => {
    recalc();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', recalc, { passive: true });
    window.addEventListener('resize', recalc);
    return () => {
      el.removeEventListener('scroll', recalc);
      window.removeEventListener('resize', recalc);
    };
  }, []);

  const scrollTabs = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div className="w-full bg-sd-dark px-4 pt-4 pb-3">
      {/* Mobile: single row with opponent + divider + paged tabs */}
      <div className="md:hidden flex items-center w-full">
        {/* Opponent / Game name (same as origin), season hidden on mobile */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-base font-bold text-white">{shortGameName}</span>
          <ShareButton contentType="game" resourceId={gameId || ''} variant="icon" currentGameId={gameId} />
        </div>

        {/* White vertical divider */}
        <div className="h-6 w-px bg-white/90 mx-2 flex-shrink-0" />

        {/* Tabs (text only, underline when active), 4-at-a-time with animated pager) */}
        <div className="relative flex-1 min-w-0 overflow-hidden">
          <div
            className="flex w-[200%] transition-transform duration-300 ease-out"
            style={{
              transform: tabPage === 0 ? 'translateX(0%)' : 'translateX(-50%)',
            }}
          >
            {/* Page 1: primary tabs */}
            <div className="w-1/2 flex items-center justify-between">
              {primaryTabs.map((t) => (
                <NavLink key={t.label} to={t.to} end={t.label === 'Details'}>
                  {({ isActive }) => {
                    const active = isActive || t.active(location.pathname);
                    return (
                      <span
                        className={`text-sm font-semibold whitespace-nowrap truncate ${
                          active ? 'text-sd-accent underline underline-offset-4' : 'text-sd-mist'
                        }`}
                      >
                        {t.label}
                      </span>
                    );
                  }}
                </NavLink>
              ))}
            </div>
            {/* Page 2: secondary tabs */}
            <div className="w-1/2 flex items-center justify-between">
              {secondaryTabs.map((t) => (
                <NavLink key={t.label} to={t.to} end={t.label === 'Details'}>
                  {({ isActive }) => {
                    const active = isActive || t.active(location.pathname);
                    return (
                      <span
                        className={`text-sm font-semibold whitespace-nowrap truncate ${
                          active ? 'text-sd-accent underline underline-offset-4' : 'text-sd-mist'
                        }`}
                      >
                        {t.label}
                      </span>
                    );
                  }}
                </NavLink>
              ))}
            </div>
          </div>
        </div>

        {/* Pager arrow - only show if both pages have tabs */}
        {primaryTabs.length > 0 && secondaryTabs.length > 0 && (
          <div className="flex items-center justify-end flex-shrink-0 mb-1">
            {tabPage === 0 ? (
              <button
                aria-label="Show more tabs"
                onClick={() => setTabPage(1)}
                className="text-white text-2xl leading-none px-1"
              >
                ›
              </button>
            ) : (
              <button
                aria-label="Show previous tabs"
                onClick={() => setTabPage(0)}
                className="text-white text-2xl leading-none px-1"
              >
                ‹
              </button>
            )}
          </div>
        )}
      </div>

      {/* Desktop: keep existing two-row layout */}
      <div className="hidden md:flex flex-col gap-4">
        {/* Row 1 */}
        <div className="w-full flex flex-row items-center justify-between gap-3">
          <div className="flex flex-col items-start">
            <div className="relative flex flex-row items-center gap-2">
              {seasonName && (
                <span className="hidden md:inline text-xl md:text-3xl font-bold text-white">{seasonName} -</span>
              )}
              <span className="text-xl md:text-3xl font-bold text-white">{gameName}</span>
              <ShareButton contentType="game" resourceId={gameId || ''} variant="icon" currentGameId={gameId} />
            </div>
          </div>
        </div>
        {/* Row 2: tabs with scroll helpers */}
        <div className="relative w-full">
          {showLeft && (
            <div
              aria-label="Scroll left"
              onClick={() => scrollTabs(-1)}
              className="absolute top-0 bottom-0 w-8 flex items-center justify-start pl-1 z-30 cursor-pointer select-none bg-gradient-to-r from-sd-dark via-sd-dark/70 to-transparent"
            >
              <span className="text-white text-3xl leading-none drop-shadow -ml-1">‹</span>
            </div>
          )}
          {showRight && (
            <div
              aria-label="Scroll right"
              onClick={() => scrollTabs(1)}
              className="absolute top-0 bottom-0 w-8 flex items-center justify-end pr-1 z-30 cursor-pointer select-none bg-gradient-to-l from-sd-dark via-sd-dark/70 to-transparent"
            >
              <span className="text-white text-3xl leading-none drop-shadow mr-0">›</span>
            </div>
          )}
          <div ref={scrollRef} className="overflow-x-auto scrollbar-hide relative z-10">
            <div className="flex gap-2 py-2 pr-4 md:pr-0 min-w-max md:min-w-0 md:justify-center md:flex-wrap">
              {tabs.map((t) => (
                <NavLink key={t.label} to={t.to} end={t.label === 'Details'}>
                  {({ isActive }) => {
                    const active = isActive || t.active(location.pathname);
                    return (
                      <div
                        className={`relative flex-none w-28 md:w-32 px-2 md:px-3 pt-2 pb-3 rounded-md text-sm font-semibold transition-all text-center ${
                          active ? 'bg-sd-accent text-black' : 'bg-sd-navy text-white'
                        }`}
                      >
                        <span>{t.label}</span>
                      </div>
                    );
                  }}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameHeader;
